import { EmbeddedCard } from "@/components/molecules/embedded-card";
import { AuthLayout } from "@/components/molecules/auth-layout";
import { Button } from "@/components/ui/button";
import {
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Spinner } from "@/components/ui/shadcn-io/spinner";
import { useAuth } from "@/features/auth/hooks/use-auth";
import { useUser } from "@/features/auth/hooks/use-user";
import { useGReCaptcha } from "@/features/utils/captcha";
import { useTranslation } from "@/features/utils/i18n";
import { useControlledEffect } from "@/features/utils/use-controlled-effect";
import { FingerprintIcon } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { toast } from "sonner";
import { WebAuthnRegister } from "../account/sections/components/add-webauthn-key";
import { LoginForm } from "./login";

export const Login = () => {
  useGReCaptcha();
  const { t } = useTranslation();
  const { isInitializing } = useAuth();
  const { data: user } = useUser();

  if (isInitializing) {
    return (
      <div className="flex h-dvh w-screen items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (!user?.id) {
    return (
      <AuthLayout>
        <CardHeader className="-mb-4">
          <CardTitle className="text-2xl font-semibold">{t("title")}</CardTitle>
        </CardHeader>
        <LoginForm />
      </AuthLayout>
    );
  }

  return (
    <EnsureWebauthnProvider>
      <Outlet />
    </EnsureWebauthnProvider>
  );
};

export const EnsureWebauthnProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { data: user } = useUser();
  const [skipped, setSkipped] = useState(
    !!localStorage.getItem("webauthn_skipped"),
  );

  useEffect(() => {
    if (user?.webauthn_devices?.devices?.length) {
      setSkipped(true);
      localStorage.setItem("webauthn_skipped", "true");
    }
  }, [user?.webauthn_devices?.devices?.length]);

  if (user?.id && !user?.webauthn_devices?.devices?.length && !skipped) {
    return (
      <EmbeddedCard logo>
        <AdviceToAddWebauthn setSkipped={setSkipped} />
      </EmbeddedCard>
    );
  }

  return <>{children}</>;
};

export const AdviceToAddWebauthn = ({
  setSkipped,
}: {
  setSkipped?: (skipped: boolean) => void;
}) => {
  const { t } = useTranslation();
  const { data: user } = useUser();
  const [addSecurityKey, setAddSecurityKey] = useState(false);

  useControlledEffect(() => {
    if (user?.webauthn_devices?.devices?.length) {
      setSkipped?.(true);
      localStorage.setItem("webauthn_skipped", "true");
    }
  }, [user?.webauthn_devices?.devices?.length]);

  return (
    <>
      <CardHeader>
        <CardTitle>{t("securityKey.title")}</CardTitle>
        <CardDescription>{t("securityKey.description")}</CardDescription>
      </CardHeader>
      <CardContent className="text-center space-y-2 mt-4">
        <Button onClick={() => setAddSecurityKey(true)} className="w-full">
          <FingerprintIcon className="mr-2" />
          {t("securityKey.addKey")}
        </Button>
        {addSecurityKey && (
          <WebAuthnRegister
            open={true}
            name={t("securityKey.initialDeviceName")}
            onSuccess={() => {
              toast.success(t("securityKey.added"));
            }}
            onOpenChange={setAddSecurityKey}
          />
        )}
        <br />
        <Button
          variant="link"
          onClick={() => {
            setSkipped?.(true);
            localStorage.setItem("webauthn_skipped", "true");
          }}
        >
          {t("securityKey.skipForNow")}
        </Button>
      </CardContent>
    </>
  );
};
